
import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

const ForestPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/db3ecdd614161d77b101e45f0d0609cb.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-black/30"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">Forest Sanctuaries</h1>
          <p className="text-xl md:text-2xl mb-8 leading-relaxed">
            Reconnect with nature in our tranquil forest retreats, where ancient trees and peaceful woodlands create the perfect escape
          </p>
          <Link 
            to="/book" 
            className="bg-green-600 hover:bg-green-7 text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-block"
          >
            Book Forest Experience
          </Link>
        </div>
      </section>

      {/* Experience Overview */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Woodland Wellness</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our forest retreats offer a sanctuary among towering trees and pristine woodlands. Experience the 
                therapeutic benefits of forest bathing, where the natural aromatherapy of pine and cedar creates 
                a healing environment for mind, body, and soul.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Each forest location features sustainably designed accommodations that blend seamlessly with the 
                natural environment, offering luxury amenities while maintaining minimal environmental impact.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">500+</div>
                  <div className="text-gray-600">Tree Species</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">15</div>
                  <div className="text-gray-600">Forest Locations</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Luxury%20treehouse%20accommodation%20in%20dense%20forest%2C%20elevated%20wooden%20structure%20with%20large%20windows%2C%20wraparound%20deck%20among%20tree%20canopy%2C%20sustainable%20architecture%20blending%20with%20forest%20environment%2C%20premium%20eco-friendly%20design%20with%20natural%20materials&width=600&height=400&seq=forest-glamp&orientation=landscape"
                alt="Forest Glamping"
                className="rounded-lg shadow-xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section className="py-20 bg-green-50 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Forest Activities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Immerse yourself in nature with activities designed to connect you with the forest ecosystem
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-leaf-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Forest Bathing</h3>
              <p className="text-gray-700 leading-relaxed">
                Practice the Japanese art of Shinrin-yoku, mindfully immersing yourself in the forest atmosphere for wellness and stress relief.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-search-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Wildlife Tracking</h3>
              <p className="text-gray-700 leading-relaxed">
                Learn to identify animal tracks, bird calls, and forest signs with expert naturalist guides on educational nature walks.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-fire-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Bushcraft Skills</h3>
              <p className="text-gray-700 leading-relaxed">
                Master traditional outdoor skills including fire-making, shelter building, and foraging with experienced wilderness instructors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Locations */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured Forest Locations</h2>
            <p className="text-xl text-gray-600">Discover our most enchanting woodland retreat destinations</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Pacific%20Northwest%20old%20growth%20forest%20with%20massive%20cedar%20and%20fir%20trees%2C%20luxury%20eco-lodge%20among%20towering%20trees%2C%20misty%20forest%20atmosphere%2C%20fern%20understory%2C%20sustainable%20architecture%20harmonizing%20with%20ancient%20woodland%20environment&width=600&height=300&seq=pacific-forest&orientation=landscape"
                alt="Pacific Northwest Forest"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Pacific Northwest Grove</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Experience the majesty of ancient old-growth forests with towering cedars and pristine wilderness trails.
                </p>
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    <i className="ri-map-pin-line mr-2"></i>Oregon, USA
                  </div>
                  <a 
                    href="/retreats" 
                    className="text-green-600 hover:text-green-700 font-semibold cursor-pointer whitespace-nowrap"
                  >
                    Learn More <i className="ri-arrow-right-line ml-1"></i>
                  </a>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Appalachian%20mountain%20forest%20with%20mixed%20hardwood%20trees%2C%20luxury%20cabin%20retreat%20among%20colorful%20autumn%20foliage%2C%20rolling%20forested%20hills%2C%20peaceful%20woodland%20setting%20with%20streams%20and%20wildlife%2C%20premium%20eco-accommodation&width=600&height=300&seq=appalachian-forest&orientation=landscape"
                alt="Appalachian Forest Retreat"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Appalachian Woodlands</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Discover the biodiversity of eastern hardwood forests with seasonal colors and abundant wildlife viewing opportunities.
                </p>
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    <i className="ri-map-pin-line mr-2"></i>Virginia, USA
                  </div>
                  <a 
                    href="/retreats" 
                    className="text-green-600 hover:text-green-700 font-semibold cursor-pointer whitespace-nowrap"
                  >
                    Learn More <i className="ri-arrow-right-line ml-1"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-green-600 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Embrace the Forest Experience</h2>
          <p className="text-xl text-green-100 mb-8 leading-relaxed">
            Book your forest retreat today and discover the healing power of nature's most peaceful environments
          </p>
          <Link 
            to="/book" 
            className="bg-white text-green-600 hover:bg-gray-100 px-8 py-4 rounded-full text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap inline-block"
          >
            Book Your Forest Retreat
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ForestPage;
